/**
 * API 키 및 라이선스 키 관리 서비스
 * Firestore를 통한 키 정보 CRUD 작업 처리
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt } from './webCryptoService';


/**
 * API/라이선스 키 추가
 * @param {string} userId - 사용자 ID
 * @param {Object} keyData - 키 정보
 * @returns {Promise<string>} 생성된 키 ID
 */
export async function addApiKey(userId, keyData) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 API 키 추가 중...');
    }

    // 민감한 데이터 암호화
    const encryptedData = {
      ...keyData,
      type: 'apikey',
      keyName: await encrypt(keyData.keyName, userId),
      keyValue: await encrypt(keyData.keyValue, userId),
      isFavorite: keyData.isFavorite || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(
      collection(db, `users/${userId}/apikeys`),
      encryptedData
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API 키 추가 완료');
    }

    return docRef.id;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 키 추가 실패:', error);
    }
    throw new Error('API 키를 추가할 수 없습니다.');
  }
}

/**
 * 사용자의 모든 API 키 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} API 키 목록
 */
export async function getApiKeys(userId) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 API 키 목록 조회 중...');
    }

    const q = query(
      collection(db, `users/${userId}/apikeys`),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const keys = [];

    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      
      // 키 이름만 복호화 (키 값은 보안상 복호화하지 않음)
      let keyName = data.keyName;
      
      // keyName이 암호화되어 있는지 확인
      if (data.keyName && typeof data.keyName === 'string' && data.keyName.includes(':')) {
        try {
          keyName = await decrypt(data.keyName, userId);
        } catch (error) {
          // 암호화되지 않은 레거시 데이터인 경우 그대로 사용
          console.log('⚠️ keyName 복호화 실패, 평문으로 처리:', error.message);
        }
      }
      
      const decryptedKey = {
        ...data,
        id: doc.id,
        keyName: keyName,
        keyValue: data.keyValue, // 암호화된 상태로 유지
        keyValueMasked: '••••••••••••••••', // 보안상 항상 마스킹
        isFavorite: data.isFavorite || false,
        itemType: 'apikey'
      };

      // 만료 여부 확인
      if (data.expiresAt) {
        const expiryDate = data.expiresAt.toDate();
        decryptedKey.isExpired = expiryDate < new Date();
        decryptedKey.daysUntilExpiry = Math.ceil(
          (expiryDate - new Date()) / (1000 * 60 * 60 * 24)
        );
      }

      keys.push(decryptedKey);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API 키 ${keys.length}개 조회 완료`);
    }

    return keys;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 키 조회 실패:', error);
    }
    throw new Error('API 키를 조회할 수 없습니다.');
  }
}

/**
 * API 키 정보 수정
 * @param {string} userId - 사용자 ID
 * @param {string} keyId - 키 ID
 * @param {Object} updates - 수정할 데이터
 * @returns {Promise<void>}
 */
export async function updateApiKey(userId, keyId, updates) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 API 키 수정 중...');
    }

    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // 민감한 데이터가 변경된 경우 암호화
    if (updates.keyValue) {
      updateData.keyValue = await encrypt(updates.keyValue, userId);
    }
    if (updates.keyName) {
      updateData.keyName = await encrypt(updates.keyName, userId);
    }

    await updateDoc(
      doc(db, `users/${userId}/apikeys`, keyId),
      updateData
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API 키 수정 완료');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 키 수정 실패:', error);
    }
    throw new Error('API 키를 수정할 수 없습니다.');
  }
}

/**
 * API 키 값 복호화 (보기 버튼 클릭 시 사용)
 * @param {string} userId - 사용자 ID
 * @param {string} encryptedKeyValue - 암호화된 키 값
 * @returns {Promise<string>} 복호화된 키 값
 */
export async function decryptApiKeyValue(userId, encryptedKeyValue) {
  try {
    return await decrypt(encryptedKeyValue, userId);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 키 복호화 실패:', error);
    }
    throw new Error('API 키를 복호화할 수 없습니다.');
  }
}

/**
 * API 키 삭제
 * @param {string} userId - 사용자 ID
 * @param {string} keyId - 키 ID
 * @returns {Promise<void>}
 */
export async function deleteApiKey(userId, keyId) {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 API 키 삭제 중...');
    }

    await deleteDoc(doc(db, `users/${userId}/apikeys`, keyId));

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API 키 삭제 완료');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 키 삭제 실패:', error);
    }
    throw new Error('API 키를 삭제할 수 없습니다.');
  }
}

/**
 * 만료 예정인 키 조회
 * @param {string} userId - 사용자 ID
 * @param {number} days - 만료까지 남은 일수
 * @returns {Promise<Array>} 만료 예정 키 목록
 */
export async function getExpiringKeys(userId, days = 30) {
  try {
    const keys = await getApiKeys(userId);
    const expiringKeys = keys.filter(key => {
      if (!key.expiresAt) return false;
      return key.daysUntilExpiry >= 0 && key.daysUntilExpiry <= days;
    });

    return expiringKeys;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ 만료 예정 키 조회 실패:', error);
    }
    return [];
  }
}


/**
 * 키 타입별 아이콘 반환
 * @param {string} keyType - 키 타입
 * @returns {string} 아이콘 이름
 */
export function getKeyTypeIcon(keyType) {
  const icons = {
    api: '🔌',
    license: '📜',
    ssh: '🔐',
    token: '🎫',
    secret: '🤫',
    certificate: '📋'
  };

  return icons[keyType] || '🔑';
}

/**
 * 키 제공자별 색상 반환
 * @param {string} provider - 제공자
 * @returns {string} 색상 코드
 */
export function getProviderColor(provider) {
  const colors = {
    'OpenAI': '#10A37F',
    'AWS': '#FF9900',
    'Google': '#4285F4',
    'Microsoft': '#0078D4',
    'GitHub': '#24292E',
    'Stripe': '#635BFF',
    'Twilio': '#F22F46'
  };

  return colors[provider] || '#6B7280';
}

/**
 * 환경별 뱃지 색상
 * @param {string} environment - 환경
 * @returns {string} 색상 클래스
 */
export function getEnvironmentBadgeClass(environment) {
  const classes = {
    production: 'badge-danger',
    development: 'badge-warning',
    test: 'badge-info',
    staging: 'badge-secondary'
  };

  return classes[environment] || 'badge-default';
}

export default {
  addApiKey,
  getApiKeys,
  updateApiKey,
  deleteApiKey,
  decryptApiKeyValue,
  getExpiringKeys,
  getKeyTypeIcon,
  getProviderColor,
  getEnvironmentBadgeClass
};